import { Button, Card, Text, Toolbar, VSpacer } from '@/components/DesignSystem';
import { FullscreenDialog } from '@/components/DesignSystem/FullscreenDialog/FullscreenDialog';
import { Table } from '@/components/DesignSystem/Table/Table';
import { FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { OrderDetailsModal } from '@/components/shared/OrderDetailsModal';
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useSearch } from '@/hooks/useSearch';
import { AboutAccountRolesBanner } from '@/pages/CustomerDashboard/LinkedAccounts';
import {
  ManualPointsHistoryDetailsModal,
} from '@/pages/CustomerDashboard/PointsActivity/ManualPointsHistoryDetailsModal';
import { DashboardApi } from '@/utilities/api/DashboardApi';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { PointsHistoryEndpoint } from '@api/endpoints/PointsHistoryEndpoint';
import { ApiSubwallet } from '@api/interfaces';
import { ApiOrderTransaction } from '@api/interfaces/ApiOrderTransaction';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import { CircularProgress, Container, Stack, useTheme } from '@mui/material';
import { CIBPageName } from '@shared/enums';
import { SharedConfig } from '@shared/SharedConfig';
import { filterToViewableCustomers, getAccountName } from '@shared/utilities/UserUtilities';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { EditManualPointsDialog } from './EditManualPointsDialog';
import { PointsActivityTableRow } from './PointsActivityTableRow';

interface PointsActivityModalProps {
  customerId: string;
  onClose: () => void;
}

export const PointsActivityModal: React.FC<PointsActivityModalProps> = ({
  customerId,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isInternal } = useAuthentication();
  const { debouncedSearch, setSearch } = useSearch(0);
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => {
    const map = new Map();
    map.set('transactionType', new Set(['all']));
    return map;
  });
  const [selectedTransaction, setSelectedTransaction] = useState<ApiOrderTransaction | null>(null);
  const [pointsHistoryToEdit, setPointsHistoryToEdit] = useState<ApiPointsHistory | null>(null);
  const [selectedPointsHistory, setSelectedPointsHistory] = useState<ApiPointsHistory | null>(
    null,
  );

  const { data: customer } = useQuery(
    [QueryKeys.GET_USER, customerId],
    () => UserApi.get(customerId),
    { enabled: !!customerId },
  );

  const { data: retailer } = useQuery(
    [QueryKeys.GET_RETAILER_BY_ID, customer?.retailerId],
    () => RetailerApi.get(customer!.retailerId!),
    { enabled: !!customer?.retailerId },
  );

  const { data: balances } = useQuery(
    [QueryKeys.LIST_SUBWALLET_BALANCES, customerId],
    () => UserApi.getSubwalletBalances(customerId),
  );

  const subwallets: ApiSubwallet[] = useMemo(() => (
    [...new Map(balances?.map(({ subwallet }) => [subwallet.id, subwallet])).values()]
  ), [balances]);

  const { data: linkedAccounts } = useQuery(
    [QueryKeys.GET_LINKED_ACCOUNTS, customerId],
    () => UserApi.getLinkedAccounts(customerId),
    { enabled: !!customerId },
  );

  const selectedTransactionType = useMemo(() => {
    const typeSet = filterSelections.get('transactionType');
    if (!typeSet || typeSet.size === 0) {
      return 'all';
    }
    return Array.from(typeSet)[0] as string;
  }, [filterSelections]);

  const activityQuery = useMemo(() => {
    const customerIds = filterSelections.get('customer')
      && Array.from(filterSelections.get('customer')!);
    const query: PointsHistoryEndpoint.List.Query = {
      limit: SharedConfig.maxPageLimit,
    };
    if (debouncedSearch?.trim()) {
      query.search = debouncedSearch.trim();
    }
    if (selectedTransactionType !== 'all') {
      query.transactionType = selectedTransactionType as 'order' | 'scale-ticket' | 'settlement';
    }
    if (customerIds && customerIds.length > 0) {
      query.customerIds = customerIds as string[];
    }
    return query;
  }, [debouncedSearch, filterSelections, selectedTransactionType]);

  const { data: activityResponse, isFetching } = useQuery(
    [QueryKeys.GET_DASHBOARD_ORDERS, customerId, activityQuery],
    () => DashboardApi.listOrderPointActivity(customerId, activityQuery),
    { enabled: !!customerId },
  );

  const allRows = useMemo(() => {
    return activityResponse?.data ?? [];
  }, [activityResponse]);

  const { data: hasTransactionSubtypes } = useQuery(
    [QueryKeys.GET_ORDERS_HAS_TRANSACTION_SUBTYPES, customer?.retailerId],
    () => OrderTransactionsApi.hasTransactionSubtypes({
      retailerId: customer!.retailerId!,
    }),
    { enabled: !!customer?.retailerId },
  );

  const customerFilterOptions = useMemo(() => {
    const options: { id: string, label: string }[] = [];
    const viewableCustomers = (!customer || !linkedAccounts)
      ? []
      : filterToViewableCustomers(customer, linkedAccounts);
    if (viewableCustomers.length > 1) {
      viewableCustomers.forEach((account) => {
        options.push({
          id: account.id,
          label: getAccountName(account),
        });
      });
    }
    return options;
  }, [customer, linkedAccounts]);

  const hasLinkedAccounts = useMemo(() => {
    return linkedAccounts && linkedAccounts.length > 1;
  }, [linkedAccounts]);

  const transactionTypeFilterOptions = useMemo(() => {
    const options = [
      { id: 'all', label: t($ => $.common.filters.all), default: true },
      { id: 'order', label: t($ => $.customers.transactions.filters.orders) },
    ];
    if (hasTransactionSubtypes?.hasScaleTicket) {
      options.push({
        id: 'scale-ticket',
        label: t($ => $.customers.transactions.filters.scaleTickets),
      });
    }
    if (hasTransactionSubtypes?.hasSettlement) {
      options.push({
        id: 'settlement',
        label: t($ => $.customers.transactions.filters.settlements),
      });
    }
    options.push({
      id: 'adjustment',
      label: t($ => $.customerDashboard.pointsActivity.adjustment),
    });
    return options;
  }, [hasTransactionSubtypes, t]);

  const filters = useMemo(() => {
    const filterArray = [];
    if (hasLinkedAccounts) {
      filterArray.push({
        id: 'customer',
        label: t($ => $.common.customer),
        options: customerFilterOptions,
        selectionMethod: 'multi-select' as const,
      });
    }
    filterArray.push({
      id: 'transactionType',
      label: t($ => $.customers.transactions.filters.transactionType),
      options: transactionTypeFilterOptions,
      selectionMethod: 'single-select' as const,
    });
    return filterArray;
  }, [hasLinkedAccounts, customerFilterOptions, transactionTypeFilterOptions, t]);

  if (!customer) {
    return (
      <FullscreenDialog
        onClose={onClose}
        open
        testID="points-activity-modal"
        title={t($ => $.customers.details.sections.pointsActivity)}
      >
        <Container maxWidth="lg">
          <Stack alignItems="center" justifyContent="center" minHeight="400px">
            <CircularProgress />
          </Stack>
        </Container>
      </FullscreenDialog>
    );
  }

  return (
    <FullscreenDialog
      actionButton={() => (
        <>
          {/*Not yet implemented https://growersnc.atlassian.net/browse/G1-1704*/}
          {/*<Button*/}
          {/*  startIcon={<ExitToApp />}*/}
          {/*  testID="points-activity-export-button"*/}
          {/*  variant="text"*/}
          {/*>*/}
          {/*  Export*/}
          {/*</Button>*/}
          {/*<HSpacer size="2" />*/}
          <Button
            onClick={onClose}
            testID="points-activity-close-button"
            variant="contained"
          >
            {t($ => $.common.actions.close)}
          </Button>
        </>
      )}
      hideCloseButton
      hideScrollbar
      hideTitleBorder={true}
      onClose={onClose}
      open
      testID="points-activity-modal"
      title={t($ => $.customers.details.sections.pointsActivity)}
    >
      {!!customer.accountGroupRole && !!linkedAccounts?.length && (
        <>
          <AboutAccountRolesBanner
            customerRole={customer.accountGroupRole}
            linkedAccounts={linkedAccounts}
            pageName={CIBPageName.RP_PointsActivity}
          />
          <VSpacer size="5" />
        </>
      )}
      <Stack>
        {customer && (
          <Text
            category="body-medium"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            {t($ => $.common.filters.account)}: {getAccountName(customer)}
          </Text>
        )}
        <Toolbar
          filterSelections={filterSelections}
          filters={filters}
          onChange={({ search, selections }) => {
            setSearch(search ?? '');
            const newSelections = selections ?? new Map();
            if (!newSelections.has('transactionType')
              || newSelections.get('transactionType')?.size === 0) {
              newSelections.set('transactionType', new Set(['all']));
            }
            setFilterSelections(newSelections);
          }}
          searchPlaceholder={t($ => $.common.search.searchById)}
          showClearAllButton
          testID="points-activity-toolbar"
          totalItems={allRows.length}
        />

        <VSpacer size="4" />

        {!isFetching && allRows.length === 0 && (
          <Card testID={""}>
            <Stack alignItems="center" justifyContent="center" minHeight="200px" py="40px">
              <Text category="body-large" sx={{ color: theme.palette.text.secondary }}>
                {t($ => $.customers.details.sections.noPointsActivity)}
              </Text>
            </Stack>
          </Card>
        )}

        {!isFetching && allRows.length > 0 && (
          <Card
            cardContentStyle={{
              padding: 0,
              '&:last-child': { paddingBottom: 0 },
            }}
            testID={""}
          >
            <Table
              headerSx={{
                backgroundColor: 'transparent !important',
                borderBottom: `1px solid ${theme.palette.divider} !important`,
              }}
              headerWidths={[120, 200, 200, 200, 120, 120, 120, 100]}
              headers={[
                t($ => $.common.table.date),
                t($ => $.customers.transactions.filters.orderId),
                t($ => $.customers.transactions.description),
                t($ => $.common.customer),
                t($ => $.customers.transactions.pointsEarned),
                t($ => $.customers.transactions.pointsRedeemed),
                t($ => $.common.table.total),
                '',
              ]}
              sx={{
                '& .MuiTableCell-root:last-child': {
                  paddingBottom: 0,
                },
              }}
            >
              {allRows.map((row, index) => {
                const isLast = index === allRows.length - 1;
                if (row.rowType === 'order') {
                  return (
                    <PointsActivityTableRow
                      key={row.order.id}
                      onDetailsClick={() => setSelectedTransaction(row.order)}
                      retailer={retailer}
                      showBottomBorder={isLast}
                      transaction={row.order}
                    />
                  );
                }
                return (
                  <PointsActivityTableRow
                    key={row.pointsHistory.id}
                    onDetailsClick={() => setSelectedPointsHistory(row.pointsHistory)}
                    pointsHistory={row.pointsHistory}
                    retailer={retailer}
                    showBottomBorder={isLast}
                  />
                );
              })}
            </Table>
          </Card>
        )}

        {isFetching && (
          <Stack alignItems="center" justifyContent="center" minHeight="400px">
            <CircularProgress />
          </Stack>
        )}
      </Stack>
      {!!selectedTransaction && (
        <OrderDetailsModal
          onClose={() => setSelectedTransaction(null)}
          onEditManualPoints={isInternal ? setPointsHistoryToEdit : undefined}
          orderId={selectedTransaction.id}
          subwallets={subwallets}
        />
      )}
      {!!selectedPointsHistory && (
        <ManualPointsHistoryDetailsModal
          onClose={() => setSelectedPointsHistory(null)}
          onEdit={isInternal && selectedPointsHistory.isManuallyIssued
            ? () => setPointsHistoryToEdit(selectedPointsHistory)
            : undefined}
          pointsHistory={selectedPointsHistory}
        />
      )}
      {!!pointsHistoryToEdit && (
        <EditManualPointsDialog
          farmerId={customerId}
          onClose={() => setPointsHistoryToEdit(null)}
          onSaved={() => {
            setPointsHistoryToEdit(null);
            setSelectedPointsHistory(null);
          }}
          pointsHistory={pointsHistoryToEdit}
        />
      )}
    </FullscreenDialog>
  );
};
